import React, { useState, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Upload, FileText, Image, Receipt, Award, Camera, 
  File, X, Check, Loader2, FolderOpen, Tag, MessageSquare,
  User, Briefcase, Home, Car, Shield, CreditCard
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import api from '../../services/api';

// Category definitions with icons and where they route to
const UPLOAD_CATEGORIES = {
  owner: [
    { id: 'drivers_license', label: "Driver's License", icon: Car, color: 'text-amber-500', destination: 'drivers_license', requiresWorker: true },
    { id: 'receipt', label: 'Receipt', icon: Receipt, color: 'text-green-500', destination: 'receipts' },
    { id: 'worker_document', label: 'Worker Document', icon: FileText, color: 'text-blue-500', destination: 'worker_documents' },
    { id: 'customer_file', label: 'Customer File', icon: User, color: 'text-purple-500', destination: 'customer_files' },
    { id: 'policy', label: 'Policy Document', icon: Shield, color: 'text-orange-500', destination: 'policies' },
    { id: 'certification', label: 'Certification', icon: Award, color: 'text-yellow-500', destination: 'certifications' },
    { id: 'photo', label: 'Photo/Image', icon: Image, color: 'text-pink-500', destination: 'gallery' },
    { id: 'contract', label: 'Contract', icon: FileText, color: 'text-indigo-500', destination: 'contracts' },
    { id: 'invoice', label: 'Invoice', icon: CreditCard, color: 'text-cyan-500', destination: 'invoices' },
    { id: 'other', label: 'Other', icon: File, color: 'text-gray-500', destination: 'general' },
  ],
  worker: [
    { id: 'drivers_license', label: "Driver's License", icon: Car, color: 'text-amber-500', destination: 'drivers_license' },
    { id: 'receipt', label: 'Receipt', icon: Receipt, color: 'text-green-500', destination: 'receipts' },
    { id: 'job_photo', label: 'Job Photo', icon: Camera, color: 'text-blue-500', destination: 'job_photos' },
    { id: 'before_photo', label: 'Before Photo', icon: Image, color: 'text-orange-500', destination: 'before_photos' },
    { id: 'after_photo', label: 'After Photo', icon: Image, color: 'text-green-500', destination: 'after_photos' },
    { id: 'id_document', label: 'ID Document', icon: User, color: 'text-purple-500', destination: 'id_documents' },
    { id: 'certification', label: 'Certification', icon: Award, color: 'text-yellow-500', destination: 'certifications' },
    { id: 'vehicle_doc', label: 'Vehicle Document', icon: Car, color: 'text-cyan-500', destination: 'vehicle_documents' },
    { id: 'other', label: 'Other', icon: File, color: 'text-gray-500', destination: 'general' },
  ],
  customer: [
    { id: 'receipt', label: 'Receipt', icon: Receipt, color: 'text-green-500', destination: 'receipts' },
    { id: 'home_photo', label: 'Home Photo', icon: Home, color: 'text-blue-500', destination: 'home_photos' },
    { id: 'before_photo', label: 'Before Service Photo', icon: Image, color: 'text-orange-500', destination: 'before_photos' },
    { id: 'special_instructions', label: 'Special Instructions Image', icon: MessageSquare, color: 'text-purple-500', destination: 'instructions' },
    { id: 'id_document', label: 'ID Document', icon: User, color: 'text-indigo-500', destination: 'id_documents' },
    { id: 'other', label: 'Other', icon: File, color: 'text-gray-500', destination: 'general' },
  ]
};

const UniversalUploader = ({ userRole = 'owner', onUploadComplete }) => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [description, setDescription] = useState('');
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [recentUploads, setRecentUploads] = useState([]);
  const [showCategoryPicker, setShowCategoryPicker] = useState(false);
  const [workers, setWorkers] = useState([]);
  const [selectedWorkerId, setSelectedWorkerId] = useState('');

  const categories = UPLOAD_CATEGORIES[userRole] || UPLOAD_CATEGORIES.customer;

  // Fetch workers list for owner uploads that require worker selection
  useEffect(() => {
    if (userRole === 'owner') {
      api.get('/workers')
        .then(res => {
          // Handle both array response and {workers: []} response
          const workersData = Array.isArray(res.data) ? res.data : (res.data?.workers || []);
          setWorkers(workersData);
        })
        .catch(err => console.error('Failed to fetch workers:', err));
    }
  }, [userRole]);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      setSelectedFiles(prev => [...prev, ...files]);
      setShowCategoryPicker(true);
    }
  }, []);

  const handleFileSelect = (e) => {
    // Prevent any form submission or navigation
    e.preventDefault();
    e.stopPropagation();
    
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      setSelectedFiles(prev => [...prev, ...files]);
      setShowCategoryPicker(true);
    }
    
    // Clear input to allow selecting the same file again
    e.target.value = '';
  };

  const removeFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    if (selectedFiles.length <= 1) {
      setShowCategoryPicker(false);
      setSelectedCategory(null);
    }
  };

  const handleUpload = async () => {
    if (!selectedFiles.length || !selectedCategory) {
      toast.error('Please select files and a category');
      return;
    }

    // Check if worker selection is required but not provided
    if (selectedCategory.requiresWorker && !selectedWorkerId) {
      toast.error('Please select a worker for this upload');
      return;
    }

    setUploading(true);

    try {
      const uploadPromises = selectedFiles.map(async (file) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('category', selectedCategory.id);
        formData.append('destination', selectedCategory.destination);
        formData.append('description', description);
        formData.append('label', selectedCategory.label);
        
        // Include worker_id for categories that require it (like driver's license)
        if (selectedCategory.requiresWorker && selectedWorkerId) {
          formData.append('worker_id', selectedWorkerId);
        }

        const response = await api.post('/uploads/universal', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        return {
          filename: file.name,
          category: selectedCategory.label,
          ...response.data
        };
      });

      const results = await Promise.all(uploadPromises);
      
      setRecentUploads(prev => [...results, ...prev].slice(0, 5));
      
      // Custom success message for driver's license
      if (selectedCategory.id === 'drivers_license' && selectedWorkerId) {
        const worker = workers.find(w => (w.user_id || w.id) === selectedWorkerId);
        toast.success(`Driver's License uploaded to ${worker?.full_name || 'worker'}'s file`);
      } else {
        toast.success(`${selectedFiles.length} file(s) uploaded to ${selectedCategory.label}`);
      }
      
      // Reset form
      setSelectedFiles([]);
      setSelectedCategory(null);
      setDescription('');
      setShowCategoryPicker(false);
      setSelectedWorkerId('');

      if (onUploadComplete) {
        onUploadComplete(results);
      }
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error(error.response?.data?.detail || 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Upload className="w-6 h-6 text-[var(--primary)]" />
            Upload Center
          </h2>
          <p className="text-[var(--text-muted)] mt-1">
            Upload files and they&apos;ll automatically go to the right place
          </p>
        </div>
      </div>

      {/* Drop Zone */}
      <div
        className={`relative border-2 border-dashed rounded-xl p-8 text-center transition-all duration-300 ${
          dragActive 
            ? 'border-[var(--primary)] bg-[var(--primary)]/10' 
            : 'border-[var(--border)] hover:border-[var(--primary)]/50 hover:bg-[var(--card-bg)]'
        }`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        data-testid="upload-dropzone"
      >
        <input
          type="file"
          multiple
          onChange={handleFileSelect}
          onClick={(e) => e.stopPropagation()}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv"
          data-testid="file-input"
        />
        
        <div className="space-y-4">
          <div className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center transition-colors ${
            dragActive ? 'bg-[var(--primary)]' : 'bg-[var(--primary)]/20'
          }`}>
            <Upload className={`w-8 h-8 ${dragActive ? 'text-white' : 'text-[var(--primary)]'}`} />
          </div>
          
          <div>
            <p className="text-lg font-medium">
              {dragActive ? 'Drop files here' : 'Drag & drop files here'}
            </p>
            <p className="text-[var(--text-muted)] text-sm mt-1">
              or click to browse • Images, PDFs, Documents
            </p>
          </div>
        </div>
      </div>

      {/* Selected Files & Category Picker */}
      <AnimatePresence>
        {selectedFiles.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="space-y-4"
          >
            {/* Selected Files List */}
            <div className="bg-[var(--card-bg)] border border-[var(--border)] rounded-xl p-4">
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <FolderOpen className="w-5 h-5 text-[var(--primary)]" />
                Selected Files ({selectedFiles.length})
              </h3>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {selectedFiles.map((file, index) => (
                  <div 
                    key={`${file.name}-${index}`}
                    className="flex items-center justify-between p-2 bg-[var(--background)] rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      {file.type.startsWith('image/') ? (
                        <Image className="w-5 h-5 text-pink-500" />
                      ) : (
                        <FileText className="w-5 h-5 text-blue-500" />
                      )}
                      <div>
                        <p className="font-medium text-sm truncate max-w-[200px]">{file.name}</p>
                        <p className="text-xs text-[var(--text-muted)]">{formatFileSize(file.size)}</p>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeFile(index)}
                      className="text-red-500 hover:bg-red-500/10"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>

            {/* Category Selection */}
            {showCategoryPicker && (
              <div className="bg-[var(--card-bg)] border border-[var(--border)] rounded-xl p-4">
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <Tag className="w-5 h-5 text-[var(--primary)]" />
                  What is this? (Pick a category)
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                  {categories.map((category) => {
                    const Icon = category.icon;
                    const isSelected = selectedCategory?.id === category.id;
                    return (
                      <button
                        key={category.id}
                        onClick={() => setSelectedCategory(category)}
                        className={`p-3 rounded-xl border-2 transition-all flex flex-col items-center gap-2 ${
                          isSelected
                            ? 'border-[var(--primary)] bg-[var(--primary)]/10'
                            : 'border-[var(--border)] hover:border-[var(--primary)]/50'
                        }`}
                        data-testid={`category-${category.id}`}
                      >
                        <Icon className={`w-6 h-6 ${category.color}`} />
                        <span className="text-sm font-medium">{category.label}</span>
                        {isSelected && (
                          <Check className="w-4 h-4 text-[var(--primary)]" />
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Description */}
            {selectedCategory && (
              <div className="bg-[var(--card-bg)] border border-[var(--border)] rounded-xl p-4">
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-[var(--primary)]" />
                  Add Details (Optional)
                </h3>
                <Input
                  placeholder="Add a note or description..."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="bg-[var(--background)]"
                  data-testid="upload-description"
                />
              </div>
            )}

            {/* Worker Selection - Required for categories like Driver's License */}
            {selectedCategory?.requiresWorker && userRole === 'owner' && (
              <div className="bg-[var(--card-bg)] border border-[var(--border)] rounded-xl p-4">
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <Briefcase className="w-5 h-5 text-amber-500" />
                  Select Worker <span className="text-red-400">*</span>
                </h3>
                <p className="text-sm text-[var(--text-muted)] mb-3">
                  This {selectedCategory.label} will be saved directly to the worker's file
                </p>
                <select
                  value={selectedWorkerId}
                  onChange={(e) => setSelectedWorkerId(e.target.value)}
                  className="w-full px-4 py-3 bg-[var(--background)] border border-[var(--border)] rounded-lg text-white focus:border-[var(--primary)] focus:outline-none"
                  data-testid="worker-select"
                >
                  <option value="">-- Select a worker --</option>
                  {workers.map(worker => (
                    <option key={worker.id} value={worker.user_id || worker.id}>
                      {worker.full_name} ({worker.email})
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Upload Button */}
            {selectedCategory && (
              <Button
                onClick={handleUpload}
                disabled={uploading}
                className="w-full bg-[var(--primary)] hover:bg-[var(--primary)]/90 text-black font-semibold py-6 rounded-xl"
                data-testid="upload-submit-btn"
              >
                {uploading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="w-5 h-5 mr-2" />
                    Upload to {selectedCategory.label}
                  </>
                )}
              </Button>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Recent Uploads */}
      {recentUploads.length > 0 && (
        <div className="bg-[var(--card-bg)] border border-[var(--border)] rounded-xl p-4">
          <h3 className="font-semibold mb-3 flex items-center gap-2">
            <Check className="w-5 h-5 text-green-500" />
            Recently Uploaded
          </h3>
          <div className="space-y-2">
            {recentUploads.map((upload, index) => (
              <div 
                key={`recent-${index}`}
                className="flex items-center justify-between p-2 bg-[var(--background)] rounded-lg"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center">
                    <Check className="w-4 h-4 text-green-500" />
                  </div>
                  <div>
                    <p className="font-medium text-sm">{upload.filename}</p>
                    <p className="text-xs text-[var(--text-muted)]">
                      Saved to {upload.category}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Quick Tips */}
      <div className="bg-[var(--primary)]/5 border border-[var(--primary)]/20 rounded-xl p-4">
        <h4 className="font-semibold text-[var(--primary)] mb-2">Quick Tips</h4>
        <ul className="text-sm text-[var(--text-muted)] space-y-1">
          <li>• Receipts go to your Receipts section for easy tracking</li>
          <li>• Job photos are linked to your work history</li>
          <li>• Documents are stored securely and can be accessed anytime</li>
          <li>• Max file size: 10MB per file</li>
        </ul>
      </div>
    </div>
  );
};

export default UniversalUploader;
